import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './KeyboardShortcuts.css';

function useShortcutGroups() {
    const { t } = useTranslation();
    return [
        {
            title: t('editor.formatting'),
            shortcuts: [
                { keys: ['⌘', 'B'], description: t('editor.bold') },
                { keys: ['⌘', 'I'], description: t('editor.italic') },
                { keys: ['⌘', 'U'], description: t('editor.underline') },
                { keys: ['⌘', 'E'], description: t('editor.inlineCode') },
                { keys: ['⌘', '⇧', 'X'], description: t('editor.strikethrough') },
            ],
        },
        {
            title: t('editor.structure'),
            shortcuts: [
                { keys: ['⌘', '⇧', '1'], description: t('editor.heading1') },
                { keys: ['⌘', '⇧', '2'], description: t('editor.heading2') },
                { keys: ['⌘', '⇧', '3'], description: t('editor.heading3') },
                { keys: ['⌘', '⇧', '7'], description: t('editor.orderedList') },
                { keys: ['⌘', '⇧', '8'], description: t('editor.bulletList') },
                { keys: ['⌘', '⇧', 'B'], description: t('editor.blockquote') },
                { keys: ['⌘', 'Enter'], description: t('editor.horizontalRule') },
            ],
        },
        {
            title: t('editor.editing'),
            shortcuts: [
                { keys: ['⌘', 'Z'], description: t('editor.undo') },
                { keys: ['⌘', '⇧', 'Z'], description: t('editor.redo') },
                { keys: ['⌘', 'A'], description: t('editor.selectAll') },
                { keys: ['⌘', 'X'], description: t('editor.cut') },
                { keys: ['⌘', 'C'], description: t('editor.copy') },
                { keys: ['⌘', 'V'], description: t('editor.paste') },
            ],
        },
        {
            title: t('editor.app'),
            shortcuts: [
                { keys: ['⌘', '/'], description: t('editor.toggleThisPanel') },
                { keys: ['⌘', '\\'], description: t('editor.toggleSidebar') },
                { keys: ['⌘', '⇧', '\\'], description: t('editor.toggleAiPanel') },
            ],
        },
    ];
}

function KeyboardShortcuts({ onClose }) {
    const { t } = useTranslation();
    const SHORTCUT_GROUPS = useShortcutGroups();
    const overlayRef = useRef(null);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    const handleBackdropClick = (e) => {
        if (e.target === overlayRef.current) onClose();
    };

    return (
        <div className="shortcuts-overlay" ref={overlayRef} onClick={handleBackdropClick}>
            <div className="shortcuts-modal">
                <div className="shortcuts-header">
                    <h3>{t('editor.keyboardShortcuts')}</h3>
                    <button className="shortcuts-close" onClick={onClose}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div className="shortcuts-body">
                    {SHORTCUT_GROUPS.map((group) => (
                        <div className="shortcut-group" key={group.title}>
                            <div className="shortcut-group-title">{group.title}</div>
                            {group.shortcuts.map((s, i) => (
                                <div className="shortcut-row" key={i}>
                                    <span className="shortcut-desc">{s.description}</span>
                                    <span className="shortcut-keys">
                                        {s.keys.map((k, j) => (
                                            <kbd key={j}>{k}</kbd>
                                        ))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default KeyboardShortcuts;
